import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';
import { rgba } from 'polished';

import { ChevronRightIcon } from '@treams/treams-icons';
import { Card, Subheading, Paragraph, Button } from '@treams/treams-ui';
import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import DoughnutMetric from '../DoughnutMetric';
import { useRouteMatch } from "react-router";
import { LineChartGradient } from '../../../../../../analytics/v2/metrics/AnalyticsGraphMetric';

const Container = styled(Card)`
	 background: linear-gradient(${(props) => rgba(props.theme.colors.employee, 0.1)}, white 100px);
`;

const FeedbackLink = styled(Box)`
	color: ${(props) => props.theme.colors.blue};
`;

const Delta = styled(Flex)<{ up: boolean }>`
	color: ${(props) => (props.up ? props.theme.colors.green : props.theme.colors.red)};

	svg {
		transform: rotate(${(props) => (props.up ? -90 : 90)}deg);

		path {
			fill: ${(props) => (props.up ? props.theme.colors.green : props.theme.colors.red)};
		}
	}
`;

const Arrow = styled(Box)`
	svg {
		display: block;
		width: 24px;
		height: 24px;
	}
`;

export default function EngagementMetric(props: {
	data?: graphql.AnalyticsEngagementSubcategoryScoreOverTime;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const theme = useTheme();

	const { location } = useHistory();
	const { params } = useRouteMatch<{ metricGroupID: string; metricID: string; tabID?: string }>();

	if (!props.data) {
		return (
			<Container>
				<Flex p={3} alignItems="center">
					<DoughnutMetric.Skeleton padding={0} size={50} fontSize={16} animated={false} />
					<Box ml={3}>{t('dashboard.metrics.engagement.metric.emptyTitle')}</Box>
				</Flex>
			</Container>
		);
	}

	const score = props.data.datapoints[props.data.datapoints.length - 1]?.score ?? 0.0;

	const feedbackPath = `/analytics/v2/metric/${params.metricGroupID}/${params.metricID}/feedback${location.search}#${props.data?.subcategory.id}`;

	return (
		<Container>
			<Flex alignItems="center" mb={3}>
				<Box flexGrow={0} flexShrink={0} mr={3}>
					<DoughnutMetric
						padding={0}
						size={50}
						fontSize={16}
						data={[
							{
								value: score,
								color: 'employee',
							},
							{
								value: 10.0 - score,
								color: 'employee',
								hidden: true,
							},
						]}
						digits={1}
						legend={false}
						tooltip={false}
					/>
					{typeof props.data.delta !== 'number' ? (
						<Box width={32} />
					) : (
						<Delta
							ml={4}
							alignItems="center"
							flexGrow={0}
							flexShrink={0}
							up={props.data.delta >= 0}
						>
							<Box flexGrow={0} flexShrink={0}>
								{Math.abs(props.data.delta).toFixed(1)}
							</Box>
							<Arrow>
								<ChevronRightIcon />
							</Arrow>
						</Delta>
					)}
				</Box>
				<Box flexGrow={1}>
					{props.data.datapoints.length > 1 ? (
						<LineChartGradient
							labels={props.data.datapoints.map((_) => '')}
							data={props.data.datapoints.map((point) => ({
								x: new Date(point.timestamp).getTime(),
								y: point.score ?? 0.0,
							}))}
							height={90}
							tintColor={theme.colors.employee}
							autoBound
							bounds={[0, 11]}
							minimalLayout
						/>
					) : (
						<Paragraph>{t('dashboard.metrics.engagement.metric.emptyTitle')}</Paragraph>
					)}
				</Box>
			</Flex>
			<Box>
				<Subheading py={1}>{localize(props.data.subcategory.title).text}</Subheading>
				<Paragraph>
					{props.data.subcategory.description && props.data.subcategory.description.length > 0 ? (
						localize(props.data.subcategory.description).text
					) : (
						<Paragraph>{t('dashboard.metrics.engagement.metric.emptyDescription')}</Paragraph>
					)}
				</Paragraph>
				<Button
					mt={3}
					link={feedbackPath}
					type={'card'}
					rightIcon={(<ChevronRightIcon />)}
					title={t('dashboard.metrics.engagement.metric.viewFeedback.title')}
					small
				/>
			</Box>
		</Container>
	);
}
